import { WarningIcon } from '@phosphor-icons/react';
import classNames from '@utils/classnames';
import { useState } from 'react';

import { getUserFacingError } from '@utils/get-display-error';
import { Button } from 'components/button/Button';
import { useTranslation } from 'contexts/translation-context';

export type ErrorStateVariant = 'page' | 'section' | 'inline' | 'table';

export interface ErrorStateProps {
  error?: unknown;
  title?: string;
  description?: string;
  onRetry?: () => void | Promise<unknown>;
  retryLabel?: string;
  variant?: ErrorStateVariant;
  className?: string;
}

const TITLE_KEYS = {
  network: 'errors.networkTitle',
  server: 'errors.serverTitle',
  'not-found': 'errors.notFoundTitle',
  unauthorized: 'errors.sessionTitle',
  permission: 'errors.permissionTitle',
  validation: 'errors.validationTitle',
  'rate-limit': 'errors.rateLimitTitle',
  aborted: 'errors.abortedTitle',
  unknown: 'errors.unknownTitle',
} as const;

const MESSAGE_KEYS = {
  network: 'errors.networkDescription',
  server: 'errors.serverDescription',
  'not-found': 'errors.notFoundDescription',
  unauthorized: 'errors.sessionDescription',
  permission: 'errors.permissionDescription',
  validation: 'errors.validationDescription',
  'rate-limit': 'errors.rateLimitDescription',
  aborted: 'errors.abortedDescription',
  unknown: 'errors.unknownDescription',
} as const;

export const ErrorState: React.FC<ErrorStateProps> = (props) => {
  const { error, title, description, onRetry, retryLabel, variant = 'section', className } = props;
  const { i18n } = useTranslation();
  const [isRetrying, setIsRetrying] = useState(false);
  const userFacingError = getUserFacingError(error);
  const resolvedTitle = title ?? i18n(TITLE_KEYS[userFacingError.kind]);
  const resolvedDescription =
    description ??
    (userFacingError.safeDetail && userFacingError.kind === 'validation'
      ? userFacingError.safeDetail
      : i18n(MESSAGE_KEYS[userFacingError.kind]));
  const canRetry = Boolean(onRetry && userFacingError.canRetry);
  const isCentered = variant === 'page' || variant === 'table';

  const retry = async () => {
    if (!onRetry || isRetrying) {
      return;
    }

    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div
      role="alert"
      className={classNames(
        'w-full rounded-lg border border-negative-border bg-negative-subtle text-negative-text',
        {
          'min-h-96 flex items-center justify-center p-8 border-0 bg-transparent': variant === 'page',
          'p-6': variant === 'section',
          'p-4': variant === 'inline',
          'border-0 bg-transparent p-4': variant === 'table',
        },
        className,
      )}
    >
      <div
        className={classNames('flex gap-3', {
          'max-w-xl': variant === 'page',
          'items-start': !isCentered,
          'flex-col items-center text-center mx-auto': isCentered,
        })}
      >
        <WarningIcon className={classNames('w-5 h-5 shrink-0', { 'mt-0.5': !isCentered })} weight="bold" />
        <div>
          <div className="font-medium mb-1">{resolvedTitle}</div>
          <div className="text-sm">{resolvedDescription}</div>

          {canRetry ? (
            <div className={classNames('mt-4', { 'flex justify-center': isCentered })}>
              <Button color="danger-outline" onTrigger={retry} isLoading={isRetrying}>
                {retryLabel ?? i18n('errors.retry')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
